/**
 * The name a row prints for an exploration (D105).
 *
 * An exploration has no name of its own: the seed is the words somebody typed,
 * and D90 records why those words are the seed's only honest label. But a row
 * in a list is not a card on a canvas, and a prompt that runs to a paragraph is
 * a row that cannot be spoken, dragged or confirmed by name. So a row is named
 * by its seed CLAMPED THE WAY A PROJECT LABEL IS — the same clamp, through the
 * same function, so a heading and the rows under it are one rule: trimmed,
 * cut to `PROJECT_LABEL_MAX`, and trimmed again. The full seed stays a hover
 * away, on the row's title.
 *
 * NOT A SUMMARY, and not a model's title. A six-word summary was built first
 * — the seed's opening words with a question's framing taken off — and the
 * owner asked for the clamp alone: words taken off the front are a name the
 * seed did not say, and one rule for headings and rows is one thing to learn.
 * A model would write a better title than either, and this app has a rule
 * about what a model call costs: every one of them sits behind an explicit
 * press (invariant 9), so a call minted on every seed to name its row would
 * be spending nobody pressed for.
 *
 * UNLESS SOMEBODY NAMED IT. A fork carries its source's seed word for word, so
 * a family of forks was a column of one name; the row's menu now offers Rename,
 * and a name somebody typed stands in for the seed's clamp everywhere a row is
 * named (`shownName`). It is a label over the seed, never an edit of it — see
 * `Session.name` — so clearing it gives the seed's clamp back. And a fork says
 * where it came from on the line under the name (`forkLine`), whether or not
 * anybody named it.
 *
 * The sidebar, the search sheet, the library, the palette and the tab title all
 * print through this, so a row is named the same on each. Imports only the label
 * clamp and the date helper, neither of which imports anything at runtime.
 */

import { shortDay } from './dates'
import { PROJECT_LABEL_MAX, projectLabel } from './types'
import type { ForkOrigin } from './types'

/** The most characters a name may carry: the project label's own clamp. */
export const NAME_CHARS = PROJECT_LABEL_MAX

/**
 * The name, or '' for a seed with no words in it — the caller prints its own
 * standing name for that, the way it always has. The label clamp itself, so
 * the two cannot drift: a name is exactly what the seed would be as a label.
 */
export function explorationName(seed: string): string {
  return projectLabel(seed) ?? ''
}

/**
 * What a row is called: the name somebody gave it, or its seed's clamp — or ''
 * for neither, which the caller prints as its standing name. Takes an index row
 * or a session alike; both carry `seed` and, once somebody named it, `name`.
 */
export function shownName(e: { seed: string; name?: string }): string {
  return projectLabel(e.name) ?? explorationName(e.seed)
}

/** What a row, or the open exploration, is called wherever it is named: its
 *  shown name, else the standing name for a seed with no words in it (D105).
 *  The sidebar's rows and the thread column's head say it through here. */
export function nameOrUntitled(e: { seed: string; name?: string }): string {
  return shownName(e) || 'Untitled exploration'
}

/**
 * The ONE normalisation of a typed name, for an exploration whose seed is `seed`:
 * the label clamp, and absence for a name that says exactly what the seed would
 * print anyway.
 *
 * That second rule is what makes the field safe to open and close. It arrives
 * holding the row's current name, and pressing Enter over the seed's own clamp
 * would otherwise store a copy of the seed's words as a "name" — one that would
 * stop following the seed and read as somebody's choice. Absence is the one
 * representation of "not named", so clearing the field and keeping the seed's
 * words are the same act. Every writer comes through here: `setName` for the
 * open exploration, `nameStored` for one on disk, and the codec reading one back.
 */
export function nameFor(raw: unknown, seed: string): string | undefined {
  const name = projectLabel(raw)
  return name !== undefined && name === explorationName(seed) ? undefined : name
}

/** "24 Sep", with every other date the app prints (`core/dates.ts`). Re-exported
 *  so the fork line's callers keep their import. */
export { shortDay }

/**
 * The line under a forked row's name: "branch at step 12 · 24 Sep", or
 * "copy · 24 Sep" for a whole-graph fork.
 *
 * Lower case, as the rest of the row's second line is. Says only what the fork
 * itself recorded — which kind, which step, which day — and never the source's
 * name, which the fork holds only as an id and which may have changed since.
 */
export function forkLine(o: ForkOrigin, now = Date.now()): string {
  const what = o.kind === 'branch' && o.step ? `branch at step ${o.step}` : o.kind
  return `${what} · ${shortDay(o.at, now)}`
}
